import { daysLeftInTrash } from '@nedelnik/shared';

import type { Task } from '@nedelnik/shared';

/**
 * Подписи корзины: когда задачу удалили и сколько она ещё пролежит.
 *
 * Срок хранения — не решение интерфейса: столько живёт надгробие до уборки на
 * сервере (`TOMBSTONE_TTL_DAYS` в `shared`). Поэтому здесь только слова, а
 * само число приходит из общего правила — иначе корзина обещала бы свой срок,
 * а уборка соблюдала бы другой.
 */

const DAY_MONTH = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' });

export interface TrashEntry {
  task: Task;
  /** «удалена 29 августа». */
  when: string;
  /** «хранится ещё 27 дней» — обещание, которое исполнит уборка. */
  keep: string;
}

export function trashEntries(tasks: readonly Task[], now: Date): TrashEntry[] {
  return tasks.map((task) => ({
    task,
    when: whenLabel(task.deleted_at),
    keep: keepLabel(task.deleted_at, now),
  }));
}

/**
 * Дата удаления словами.
 *
 * Разбор обёрнут не из перестраховки: `Intl.format` от `Invalid Date` бросает
 * `RangeError`, а в React это уносит всё дерево — страница гаснет. Тот же урок
 * уже записан в подписи срока (`ui/task/due.ts`).
 */
function whenLabel(deletedAt: string | null | undefined): string {
  if (deletedAt == null) return 'когда удалена — неизвестно';

  try {
    return `удалена ${DAY_MONTH.format(new Date(deletedAt))}`;
  } catch {
    return 'когда удалена — неизвестно';
  }
}

function keepLabel(deletedAt: string | null | undefined, now: Date): string {
  const days = deletedAt == null ? 0 : daysLeftInTrash(deletedAt, now);
  return `хранится ещё ${String(days)} ${pluralDays(days)}`;
}

/** «1 день», «2 дня», «27 дней»: число видно каждый раз, и склонение тоже. */
function pluralDays(count: number): string {
  const tail = count % 100;
  if (tail >= 11 && tail <= 14) return 'дней';

  switch (count % 10) {
    case 1:
      return 'день';
    case 2:
    case 3:
    case 4:
      return 'дня';
    default:
      return 'дней';
  }
}
